import { defineStore, storeToRefs } from 'pinia'
import * as api from '@/api'
import BppHomepage from '@/model/BppHomepage'
import { FinalPage } from '@/model'
import { BenefitsTiers, OffersEligibilityTypes, ServiceTypes } from '@/enums'
import { useFlow } from '@/store'
import { trackSummaryPrice } from '@/utils/analytics'
import Disclaimer from '@/model/Disclaimer'
import DeclineBalance from '@/model/DeclineBalance'
import GlobalAllowance from '@/model/GlobalAllowance'
import Error from '@/model/Error'

const DEFAULT_COLORS = {
  primary: '#767676',
  secondary: '#e1e1e1',
  tertiary: '#3f3f3f'
}

interface GlobalState {
  loadingGlobalConfiguration: boolean;
  loadingHomepageData: boolean;
  loadingFinalPageData: boolean;
  isErrorModalOpened: boolean;
  clientId: string;
  locale: string;
  groupCk: string;
  groupId: string;
  classId: string;
  classPlanId: string;
  showEye360: boolean;
  packageId: string;
  flowIdList: string;
  bppFlowIdList: string;
  primaryColor: string;
  secondaryColor: string;
  tertiaryColor: string;
  footerDisclaimer: string;
  disclaimerModalContent: Disclaimer;
  declineBalanceContent: DeclineBalance;
  globalAllowanceContent: GlobalAllowance;
  errorContent: Error;
  homepageData: BppHomepage;
  finalPageData: FinalPage;
  encryptedURL: string
}

const store = defineStore('global', {
  state: (): GlobalState => {
    return {
      loadingGlobalConfiguration: true,
      loadingHomepageData: true,
      loadingFinalPageData: false,
      isErrorModalOpened: false,
      clientId: '',
      groupCk: '',
      groupId: '',
      classId: '',
      classPlanId: '',
      showEye360: false,
      packageId: '',
      locale: '',
      flowIdList: '',
      bppFlowIdList: '',
      primaryColor: DEFAULT_COLORS.primary,
      secondaryColor: DEFAULT_COLORS.secondary,
      tertiaryColor: DEFAULT_COLORS.tertiary,
      footerDisclaimer: '',
      disclaimerModalContent: Disclaimer.empty(),
      declineBalanceContent: DeclineBalance.empty(),
      globalAllowanceContent: GlobalAllowance.empty(),
      errorContent: Error.default(),
      homepageData: BppHomepage.empty(),
      finalPageData: FinalPage.empty(),
      encryptedURL:''
    }
  },
  getters: {
    busy: (state: any) => {
      return state.loadingGlobalConfiguration || state.loadingHomepageData
    }
  },
  actions: {
    async getGlobalConfiguration (clientId: string, locale: string) {
      this.loadingGlobalConfiguration = true

      try {
        const { flowIdList, bppFlowIdList, primaryColor, secondaryColor, tertiaryColor, footerDisclaimer, disclaimerModalContent, declineBalanceContent, globalAllowanceContent, errorContent } = await api.cms.fetchClientConfiguration(clientId, locale)

        this.$patch({
          clientId,
          locale: locale,
          flowIdList,
          bppFlowIdList,
          primaryColor,
          secondaryColor,
          tertiaryColor,
          loadingGlobalConfiguration: false,
          footerDisclaimer,
          disclaimerModalContent,
          declineBalanceContent,
          globalAllowanceContent,
          errorContent
        })
      } catch (e) {
        this.$patch({ isErrorModalOpened: true })
      }
    },
    async getHomepageData (clientId: string, locale: string) {
      this.loadingHomepageData = true

      try {
        const homepageData = await api.cms.fetchHomepage(clientId, locale)

        this.$patch({
          homepageData,
          loadingHomepageData: false
        })
      } catch (e) {
        this.$patch({ isErrorModalOpened: true })
      }
    },
    async getFinalPageData () {
      this.loadingFinalPageData = true
      const flowStore = useFlow()
      const { serviceType, updatePriceComparisonWithEye360 } = flowStore
      const { pricingSummary } = storeToRefs(flowStore)

      try {
        const offersEligibility = this.packageId ? OffersEligibilityTypes.withOffers : OffersEligibilityTypes.withoutOffers
        const benefitsTier = this.showEye360 ? BenefitsTiers.plusProvider : BenefitsTiers.nonPlusProvider

        if (this.showEye360) {
          await updatePriceComparisonWithEye360()
        }

        const { totalMoop, saving, totalMoopNonPlus, totalCharge } = pricingSummary.value
        trackSummaryPrice(
          totalCharge,
          totalMoop,
          saving,
          this.showEye360 ? totalMoop : -1,
          totalMoopNonPlus !== null ? totalMoopNonPlus : -1
        )

        const finalPage = await api.cms.fetchFinalPage(this.clientId, this.locale, serviceType as ServiceTypes, offersEligibility, benefitsTier, this.packageId)

        this.$patch({
          finalPageData: finalPage,
          loadingFinalPageData: false
        })
      } catch (e) {
        this.$patch({ isErrorModalOpened: true })
      }
    }
  }
})

export default store
